import { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { WelcomeSection } from '../../WelcomeSection';
import { Stepper } from '../../Stepper';
import {
  OnboardingStep,
  Steps,
} from '../../../../../ui/layouts/OnboardingLayout';
import { Input } from '../../../../../ui/form/Input';
import { Select } from '../../../../../ui/form/Select';
import { FooterSection } from '../../FooterSection';
import { OnboardingData } from '../../../../../types/onboardingTypes';
import onboardingProcess from '../../../../../network/onboardingProcess';
import { fetchWorkspaceSettings } from '../../../../../routes/Settings/components/useWorkspaceSettings';

type InitialSetupValues = {
  name: string;
  company_name: string;
  primary_contact_email: string;
  office: string;
  country: string;
  location: string;
};

/** Saved details this form does not ask for but the API rewrites. */
type PreservedDetails = {
  industry?: string;
  number_of_employees?: string;
};

/**
 * The initial_setup payload. The API writes industry and size back to the
 * organisation and workspace on every submit, so they are carried over from
 * what is already saved, and empty values are left out.
 */
export function buildInitialSetupPayload(
  values: InitialSetupValues,
  preserved: PreservedDetails,
): Record<string, string> {
  const candidate: Record<string, string | undefined> = {
    ...values,
    city: values.location,
    ...preserved,
  };

  return Object.keys(candidate).reduce<Record<string, string>>(
    (payload, key) => {
      const value = candidate[key];
      if (typeof value === 'string' && value.trim() !== '') {
        payload[key] = value;
      }
      return payload;
    },
    {},
  );
}

function apiErrorMessage(error: unknown, fallback: string): string {
  const message = (error as any)?.response?.data?.message;
  return typeof message === 'string' && message ? message : fallback;
}

type Props = {
  currentStep: Steps;
  handleNext: () => void;
  handlePrevious: () => void;
  handleSkipForNow: () => void;
};

export function FirstStep({
  currentStep,
  handleNext,
  handlePrevious,
  handleSkipForNow,
}: Props) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Partial<OnboardingData>>({
    name: '',
    company_name: '',
    primary_contact_email: '',
    office: '',
    country: '',
    location: '',
  });
  const [countryOptions, setCountryOptions] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [cityOptions, setCityOptions] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [preserved, setPreserved] = useState<PreservedDetails>({});
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>(
    'loading',
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      name: formData.name || '',
      company_name: formData.company_name || '',
      primary_contact_email: formData.primary_contact_email || '',
      office: formData.office || '',
      country: formData.country || '',
      location: formData.location || formData.city || '',
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      name: yup.string().required('Name is required'),
      company_name: yup.string().required('Company name is required'),
      primary_contact_email: yup
        .string()
        .email('Enter a valid email address')
        .required('Primary contact email is required'),
      office: yup.string().required('Office is required'),
      country: yup.string().required('Country is required'),
      location: yup.string().required('Location is required'),
    }),
    onSubmit: async (data) => {
      // Without the saved details the submit would blank industry and size.
      if (loadState !== 'ready') {
        setSubmitError(
          'Your saved details have not loaded yet. Reload them and try again.',
        );
        return;
      }

      setSubmitError(null);
      try {
        await onboardingProcess.onboardingProcess(
          OnboardingStep.InitialSetup,
          buildInitialSetupPayload(data, preserved),
        );

        handleNext();
        navigate('/');
      } catch (error) {
        console.error('Failed to save onboarding details', error);
        setSubmitError(
          apiErrorMessage(
            error,
            'We could not save your details. Please try again.',
          ),
        );
      }
    },
  });

  function findError(fieldName: keyof typeof formik.initialValues) {
    return formik.touched[fieldName] && formik.errors[fieldName]
      ? formik.errors[fieldName]
      : undefined;
  }

  const loadInitialData = useCallback(async () => {
    setLoadState('loading');
    try {
      const [{ data: onboardingData }, { data: countriesOptions }, workspace] =
        await Promise.all([
          onboardingProcess.getOnboardingData(OnboardingStep.InitialSetup),
          onboardingProcess.getOnboardingCountries(),
          // Only used to keep industry and size, so setup works without it.
          fetchWorkspaceSettings().catch(() => null),
        ]);
      const saved = onboardingData?.data || {};

      setFormData({
        name: saved.name || '',
        company_name: saved.company_name || '',
        primary_contact_email: saved.primary_contact_email || '',
        office: saved.office || '',
        country: saved.country || '',
        location: saved.location || saved.city || '',
      });
      // The workspace holds the current values (Settings may have changed
      // them since onboarding); the saved setup is the fallback.
      setPreserved({
        industry: workspace?.general?.industry || saved.industry || '',
        number_of_employees:
          workspace?.general?.employees || saved.number_of_employees || '',
      });
      setCountryOptions(countriesOptions || []);
      setLoadState('ready');
    } catch (error) {
      console.error('Failed to load onboarding details', error);
      setLoadState('error');
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    let cancelled = false;

    async function fetchCities() {
      if (!formik.values.country) {
        setCityOptions([]);
        return;
      }

      try {
        const { data } = await onboardingProcess.getOnboardingCities(
          formik.values.country,
        );
        if (!cancelled) setCityOptions(data || []);
      } catch (error) {
        // Fall back to the free-text city field.
        if (!cancelled) setCityOptions([]);
      }
    }

    fetchCities();
    return () => {
      cancelled = true;
    };
  }, [formik.values.country]);

  return (
    <>
      <div className="bg-center bg-no-repeat md:w-[40%] md:bg-form-image1 " />
      <div className="m-2 flex w-screen flex-col justify-between rounded-lg bg-white px-10 pt-8 md:w-[60%] ">
        <div>
          <WelcomeSection handlePrev={handlePrevious} />
          <Stepper currentStep={currentStep} totalSteps={1} />
          <p className="mt-4 text-lg font-semibold">
            Start with the essentials so we can finish your workspace setup and
            get you into Redstone HR quickly.
          </p>
          {loadState === 'loading' && (
            <p className="mt-3 text-sm text-gray-500">
              Loading your saved details...
            </p>
          )}
          {loadState === 'error' && (
            <div
              role="alert"
              className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              <span>We could not load your saved details.</span>
              <button
                type="button"
                onClick={loadInitialData}
                className="rounded-lg border border-red-300 bg-white px-3 py-1 font-medium"
              >
                Try again
              </button>
            </div>
          )}
          {submitError && (
            <div
              role="alert"
              className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {submitError}
            </div>
          )}
          <form
            className="mt-3 h-[70vh] overflow-y-auto pr-2"
            onSubmit={formik.handleSubmit}
          >
            <div>
              <label
                htmlFor="name"
                className="mb-2 block text-base font-medium"
              >
                Name
              </label>
              <Input
                type="text"
                placeholder="Your name"
                id="name"
                name="name"
                error={findError('name')}
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
            <div className="mt-4">
              <label
                htmlFor="company_name"
                className="mb-2 block text-base font-medium"
              >
                Company name
              </label>
              <Input
                type="text"
                placeholder="Your company name"
                id="company_name"
                name="company_name"
                error={findError('company_name')}
                value={formik.values.company_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
            <div className="mt-4">
              <label
                htmlFor="primary_contact_email"
                className="mb-2 block text-base font-medium"
              >
                Primary contact email
              </label>
              <Input
                type="email"
                placeholder="name@company.com"
                id="primary_contact_email"
                name="primary_contact_email"
                error={findError('primary_contact_email')}
                value={formik.values.primary_contact_email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
            <div className="mt-4">
              <label
                htmlFor="office"
                className="mb-2 block text-base font-medium"
              >
                Office
              </label>
              <Input
                type="text"
                placeholder="Head Office"
                id="office"
                name="office"
                error={findError('office')}
                value={formik.values.office}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
            <div className="mt-4">
              <label
                htmlFor="country"
                className="mb-2 block text-base font-medium"
              >
                Country/Region
              </label>
              <Select
                id="country"
                name="country"
                value={formik.values.country}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={findError('country')}
              >
                <option value="">Select</option>
                {countryOptions.map((item: { id: string; name: string }) => (
                  <option key={item.id} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="mt-4">
              <label
                htmlFor="city"
                className="mb-2 block text-base font-medium"
              >
                Region/City
              </label>
              {cityOptions.length > 0 ? (
                <Select
                  id="location"
                  name="location"
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={findError('location')}
                >
                  <option value="">Select</option>
                  {cityOptions.map((item: { id: string; name: string }) => (
                    <option key={item.id} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  type="text"
                  placeholder="Enter your city or region"
                  id="location"
                  name="location"
                  error={findError('location')}
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              )}
            </div>
          </form>
        </div>
        <FooterSection
          nextHandle={formik.submitForm}
          handleSkipForNow={handleSkipForNow}
          nextLabel="Finish setup"
        />
      </div>
    </>
  );
}
